import { useContext } from 'react';
import { Box, Icon, Paragraph, TextButton, Tooltip, TriggerText } from '@zillow/constellation-10';
import { IconCheckmarkFilled, IconCheckmarkOutline } from '@zillow/constellation-icons';
import { AppContext } from '../../../../contexts/AppContextProvider';
import { LeadManagementContext } from '../../../../contexts/LeadManagementContextProvider';
import useQueryParamsNext from '../../../../hooks/useQueryParamsNext';
import {
  leadManagementRenterDetailsSectionToScrollToStore,
  leadManagementShowProfileRequestedForConversationIdStore,
} from '../../../../lib/leadManagementStores';
import ScreeningCriteriaMatches from '../../../ScreeningCriteriaMatches';
import type { SatelliteWebLandlordLeadManagementTableRowScreeningCriteriaDto } from '@zg-rentals/ts-satellite-client';
import useModal from '../../../../hooks/useModal';
import { ModalType } from '../../../../../src/types/ModalType';
import {
  DISPLAY_SCREENING_CRITERIA_HOVER_SCREEN_EVENT_TYPE_ID,
  emitDisplayScreeningCriteriaHoverScreenEvent,
} from '../../../../constants/clickstreamEvents/screeningCriteria';
import { useAuth } from '../../../../AuthProvider';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';

type ScreeningCriteriaTooltipProps = {
  conversationId: string;
  isPhoneLead: boolean;
  leadId?: string | null;
  screeningCriteria: SatelliteWebLandlordLeadManagementTableRowScreeningCriteriaDto;
  setActiveRowId: (conversationId: string) => void;
  shouldDisplayRequestedText: boolean;
};

export default function ScreeningCriteriaTooltip({
  conversationId,
  isPhoneLead,
  leadId,
  screeningCriteria,
  setActiveRowId,
  shouldDisplayRequestedText,
}: ScreeningCriteriaTooltipProps) {
  const { isImpersonator } = useContext(AppContext);
  const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
  const lead = useCachedLeadByConversationId(conversationId);
  const listingAlias = lead?.listingDetails?.listingAlias ?? '';
  const { updateQueryParams } = useQueryParamsNext();
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  const leadManagementShowProfileRequestedForConversationId =
    leadManagementShowProfileRequestedForConversationIdStore.useValue();
  const {
    criteria,
    numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch,
    numCriteriaSelectedByLandlord,
    criteriaMatchedDisplay,
    hasScreeningCriteria,
    hasSubmittedProfile,
    shouldShowRequestRenterProfileCta,
  } = screeningCriteria;

  const shouldShowViewProfile = !hasScreeningCriteria && hasSubmittedProfile;
  const hasMatchedAllCriteria =
    numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch !== 0 &&
    numCriteriaSelectedByLandlord !== 0 &&
    numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch === numCriteriaSelectedByLandlord;
  const shouldShowProfileRequested = leadManagementShowProfileRequestedForConversationId === conversationId;

  if (isPhoneLead || (criteria.length === 0 && !shouldShowRequestRenterProfileCta)) {
    return <Paragraph textStyle="body-sm">Not available</Paragraph>;
  }

  const handleClickstreamEvent = (isOpen: boolean) => {
    if (isOpen) {
      emitDisplayScreeningCriteriaHoverScreenEvent(user, { listingAlias }, { leadId });
    }
  };

  const handleShowRequestRenterProfileModal = () => {
    setActiveRowId(conversationId);
    setActiveModal(ModalType.RequestRenterProfileModal);
  };

  if (shouldShowRequestRenterProfileCta) {
    return (
      <TextButton
        textStyle="body-sm"
        disabled={isArchivedFolder || isReportedFolder || isImpersonator}
        onClick={handleShowRequestRenterProfileModal}
      >
        Request
      </TextButton>
    );
  }

  if (shouldShowProfileRequested) {
    return (
      <>
        {shouldDisplayRequestedText ? (
          <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
            Requested
          </Paragraph>
        ) : (
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
            <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
              Profile requested
            </Paragraph>
            <Icon size="sm" css={{ color: 'text.subtle' }}>
              <IconCheckmarkOutline />
            </Icon>
          </Box>
        )}
      </>
    );
  }

  const handleViewProfileClick = () => {
    leadManagementRenterDetailsSectionToScrollToStore.set('renterProfile');

    updateQueryParams({
      queryParamValues: {
        conversationId,
      },
      shouldReplaceHistory: true,
    });
  };

  return (
    <>
      {shouldShowViewProfile ? (
        <TextButton textStyle="body-sm" onClick={handleViewProfileClick}>
          View profile
        </TextButton>
      ) : (
        <Tooltip.Root onOpenChange={handleClickstreamEvent} placement="bottom" shouldAwaitInteractionResponse>
          <Tooltip.Trigger>
            <TriggerText data-eventid={DISPLAY_SCREENING_CRITERIA_HOVER_SCREEN_EVENT_TYPE_ID}>
              <Paragraph textStyle="body-sm" css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                {criteriaMatchedDisplay}
                {hasMatchedAllCriteria && (
                  <Icon css={{ color: 'green.600' }}>
                    <IconCheckmarkFilled />
                  </Icon>
                )}
              </Paragraph>
            </TriggerText>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content>
              <Box css={{ paddingTop: 'tighter', paddingBottom: 'tighter' }}>
                <ScreeningCriteriaMatches criteria={criteria || []} />
              </Box>
              <Tooltip.Arrow />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      )}
    </>
  );
}
